import * as Sentry from '@sentry/react';
import { db, SpraySessionStartPayload } from '../db';
import { sprayService } from './sprayService';
import { applyLocalStartedSession } from './spraySessionCacheService';

/**
 * Field Mode Start Session — online + offline-aware writer.
 *
 * Mirrors the spraySyncService pattern (queue + drainer) for the new
 * /start endpoint. Tap-to-start at the pre-flight screen flows through
 * here: the optimistic cache flip is unconditional, the network call
 * is best-effort, and a retryable failure (no signal, transient 5xx)
 * is queued for later replay. Backend rejections (PPE incomplete, not
 * assigned, already started) are NOT retryable — they propagate as
 * thrown errors so the UI can show a clean message.
 *
 * Track: spray_session_workflow_20260427 / Phase 4, Task 3.
 */

/**
 * Decide whether an error is worth re-queueing. The same shape rule
 * spraySyncService uses, extended to handle sprayService.startSession's
 * error-wrapping behaviour: when the backend responds with a 4xx/5xx
 * the service catches the original axios error, wraps it in a new
 * Error(backendMessage), and stashes the original on `.cause`. So for
 * a backend response we expect either `err.response` (raw axios) OR
 * `err.cause.response` (wrapped). Anything else is a network error
 * worth queueing.
 */
function isRetryableError(err: any): boolean {
  if (!err) return false;
  if (err.response) return false;
  if (err.cause && err.cause.response) return false;
  return true;
}

/**
 * Write a Start payload to the offline queue. Safe to call from any
 * online/offline state; never reaches the network. lastError captures
 * the most recent network failure message so the drainer's logs
 * remain auditable.
 */
export async function queueStart(
  sessionId: string,
  payload: SpraySessionStartPayload,
  lastError?: string
): Promise<number> {
  const id = await db.spraySessionStarts.add({
    sessionId,
    payload,
    syncStatus: 'pending',
    retryCount: 0,
    createdAt: new Date().toISOString(),
    lastError,
  });
  return id as number;
}

/**
 * Tap-to-start the session: try the network first, fall back to the
 * offline queue on network failure. Always flips the local cache to
 * in_progress so the UI is responsive even when the round-trip will
 * complete later.
 *
 * Backend rejections (4xx/5xx) propagate as thrown errors and do NOT
 * queue — re-trying them would never succeed.
 */
export async function start(
  sessionId: string,
  payload: SpraySessionStartPayload
): Promise<void> {
  return Sentry.startSpan(
    { name: 'spray.start.queueOrSend', op: 'http.client', attributes: { sessionId } },
    async () => {
      try {
        await sprayService.startSession(sessionId, payload);
        await applyLocalStartedSession(sessionId, payload.startedAt);
      } catch (err: any) {
        if (!isRetryableError(err)) {
          // Backend spoke — surface the message, do NOT queue.
          throw err;
        }
        // Network failure — queue and flip the cache optimistically.
        await queueStart(sessionId, payload, err?.message || 'Unknown error');
        await applyLocalStartedSession(sessionId, payload.startedAt);
      }
    }
  );
}
